import React, { useState, useEffect, useMemo, ChangeEvent } from 'react';
import { Plus } from 'lucide-react';
import { deleteContact } from 'services/api/ContactServiceAPI';
import { Select, Button, SearchBar } from 'common/components';
import UploadContactsModal from 'components/UploadContactsModal';
import ContactDrawer from './ContactDrawer';
import ContactsTable from './ContactsTable';
import { Contact, GetContactsParams, Workflow, formatStatusForDisplay } from 'types/contact.type';
import { useGetContacts } from 'services/hooks/queries/useGetContacts';
import { useLocation, useSearchParams } from 'react-router-dom';

import styles from './ContactManager.module.scss';
import { useTenantScope } from 'hooks';
import { NotificationModal } from 'components';
import type { NotificationModalProps } from 'components/NotificationModal';

// Component-specific types
interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

type NotificationModalState = Omit<NotificationModalProps, 'onClose'>;

const sortOptions: SelectOption[] = [
    { value: '', label: 'Sort by', disabled: true },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'name', label: 'Name (A-Z)' },
];

const ContactManager: React.FC = () => {
    const { isTenantScopeRequired } = useTenantScope();

    // get search params
    const location = useLocation();
    const [searchParams] = useSearchParams(location.search);
    const phoneNumber = searchParams.get('phoneNumber') || '';
    // State for contacts data
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [rawStatusOptions, setRawStatusOptions] = useState<string[]>([]);
    const [workflows, setWorkflows] = useState<Workflow[]>([]);
    const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');

    // State for filters
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [sortBy, setSortBy] = useState<GetContactsParams['sortBy']>('newest');
    const [searchTerm, setSearchTerm] = useState<string>('');

    // State for upload modal
    const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);

    // Pagination states
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalRecords, setTotalRecords] = useState<number>(0);

    // State for multiple selection
    const [selectedContacts, setSelectedContacts] = useState<string[]>([]);

    // State for contact drawer
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState('details');

    const [notificationModal, setNotificationModal] = useState<NotificationModalState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'success',
        body: null,
        buttons: [],
    });

    const statusQueryParams = useMemo(() => {
        if (!statusFilter) return undefined;
        return statusFilter.split(',').filter(Boolean);
    }, [statusFilter]);

    const statusOptions = useMemo(
        () =>
            rawStatusOptions.map((status) => ({
                value: status,
                label: formatStatusForDisplay(status),
            })),
        [rawStatusOptions]
    );

    // Load contacts on component mount and when filters change
    const { contactsData, isContactsLoading, refetchContactsData } = useGetContacts({
        page: currentPage,
        limit: rowsPerPage,
        query: searchTerm || phoneNumber,
        status: statusQueryParams,
        workflowId: selectedWorkflow,
        sortBy: sortBy,
    });

    useEffect(() => {
        if (contactsData) {
            setContacts(contactsData.contacts);

            if (contactsData.pagination) {
                setTotalPages(contactsData.pagination.totalPages || 1);
                setTotalRecords(contactsData.pagination.totalContacts || 0);

                if (contactsData.pagination.page && contactsData.pagination.page !== currentPage) {
                    setCurrentPage(contactsData.pagination.page);
                }
            }

            if (contactsData.statuses?.length && rawStatusOptions.length === 0) {
                setRawStatusOptions(contactsData.statuses);
            }

            if (contactsData.workflows?.length && workflows.length === 0) {
                setWorkflows(contactsData.workflows);
            }
        }
    }, [contactsData]);

    // Auto-adjust page if current page exceeds total pages
    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    // Check if all contacts on current page are selected
    const areAllSelected = useMemo(() => {
        return contacts.length > 0 && contacts.every((contact) => selectedContacts.includes(contact.contactId));
    }, [contacts, selectedContacts]);

    // Handle search (called by SearchBar component with debounced value)
    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    // Clear search and filters (called by SearchBar clear button)
    const handleClearSearch = () => {
        setStatusFilter('');
        setSortBy(undefined);
        setSelectedContacts([]);
        setCurrentPage(1);
    };

    const handleStatusFilterChange = (value: string) => {
        setStatusFilter(value);
        setCurrentPage(1);
    };

    // Handle workflow filter change
    const handleWorkflowFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedWorkflow(e.target.value);
        setCurrentPage(1);
    };

    // Handle sort change
    const handleSortChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSortBy(e.target.value as GetContactsParams['sortBy']);
        setCurrentPage(1);
    };

    // Handle checkbox change for single contact
    const handleContactSelect = (contactId: string) => {
        setSelectedContacts((prev) => {
            if (prev.includes(contactId)) {
                return prev.filter((id) => id !== contactId);
            } else {
                return [...prev, contactId];
            }
        });
    };

    // Handle "select all" checkbox change
    const handleSelectAll = () => {
        if (areAllSelected) {
            setSelectedContacts((prev) => prev.filter((id) => !contacts.some((contact) => contact.contactId === id)));
        } else {
            const currentPageIds = contacts.map((contact) => contact.contactId);
            setSelectedContacts((prev) => {
                const newSelection = [...prev];
                currentPageIds.forEach((id) => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                return newSelection;
            });
        }
    };

    const closeNotificationModal = () => {
        setNotificationModal((prev) => ({ ...prev, isOpen: false }));
    };

    const openNotification = (notification: NotificationModalState) => {
        setNotificationModal(notification);
    };

    // Handle bulk delete
    const handleBulkDelete = async () => {
        if (selectedContacts.length === 0) {
            openNotification({
                isOpen: true,
                title: 'Selection Required',
                message: 'Please select at least one contact to delete.',
                type: 'error',
                body: null,
                buttons: [],
            });
            return;
        }

        const contactsToDelete = [...selectedContacts];

        openNotification({
            isOpen: true,
            title: 'Confirm Delete',
            message: `Are you sure you want to delete ${contactsToDelete.length} selected contacts?`,
            type: 'error',
            body: null,
            buttons: [
                { label: 'Cancel', onClick: closeNotificationModal, variant: 'outline-secondary' },
                {
                    label: 'Delete',
                    variant: 'danger',
                    onClick: async () => {
                        closeNotificationModal();
                        try {
                            await deleteContact(contactsToDelete);

                            setContacts((prev) => prev.filter((c) => !contactsToDelete.includes(c.contactId)));
                            setSelectedContacts([]);

                            openNotification({
                                isOpen: true,
                                title: 'Deletion Successful',
                                message: `${contactsToDelete.length} contacts have been deleted successfully.`,
                                type: 'success',
                                body: null,
                                buttons: [],
                            });
                        } catch (error) {
                            console.error('Error deleting contacts:', error);
                            openNotification({
                                isOpen: true,
                                title: 'Error',
                                message: 'Error deleting contacts: ' + ((error as Error).message || 'Unknown error'),
                                type: 'error',
                                body: null,
                                buttons: [],
                            });
                        }
                    },
                },
            ],
        });
    };

    // Handle add new contact button click
    const handleAddNewContact = () => {
        if (isTenantScopeRequired) return;
        setIsUploadModalOpen(true);
    };

    // Handle upload modal close
    const handleUploadModalClose = () => {
        setIsUploadModalOpen(false);
    };

    // Handle successful upload
    const handleUploadSuccess = () => {
        openNotification({
            isOpen: true,
            title: 'Upload Successful',
            message: 'Contacts uploaded successfully.',
            type: 'success',
            body: null,
            buttons: [],
        });
        refetchContactsData();
    };

    // Handle upload error
    const handleUploadError = (message: string) => {
        openNotification({
            isOpen: true,
            title: 'Upload Failed',
            message,
            type: 'error',
            body: null,
            buttons: [],
        });
    };

    // Handle row click to open contact drawer
    const handleRowClick = async (contact: Contact) => {
        setIsDrawerOpen(true);
        setActiveTab('details');
        setSelectedContact(contact);
    };

    // Close the contact drawer
    const closeDrawer = () => {
        setIsDrawerOpen(false);
    };

    // Handle drawer tab change
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
    };

    // Pagination handlers for the Pagination component
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Contact Management</h1>
                    <p className={styles.description}>Manage and track your contacts in one place</p>
                </div>
                <Button
                    variant='primary'
                    icon={Plus}
                    iconPosition='left'
                    onClick={handleAddNewContact}
                    className={styles.addButton}
                    disabled={isTenantScopeRequired}
                >
                    Add New Contact
                </Button>
            </div>

            <div className={styles.filters}>
                <SearchBar
                    placeholder='Search contacts by name or email...'
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    debounceDelay={300}
                    className={styles.searchBox}
                />

                <div className={styles.filterOptions}>
                    <Select options={sortOptions} value={sortBy} onChange={handleSortChange} />

                    <Select
                        options={[
                            { value: '', label: 'All workflows' },
                            ...workflows.map(({ workflowId, workflowName }) => ({
                                value: workflowId,
                                label: workflowName,
                            })),
                        ]}
                        value={selectedWorkflow}
                        onChange={handleWorkflowFilterChange}
                    />
                </div>
            </div>

            <ContactsTable
                contacts={contacts}
                loading={isContactsLoading}
                selectedContacts={selectedContacts}
                areAllSelected={areAllSelected}
                searchTerm={searchTerm}
                statusFilter={statusFilter}
                statusOptions={statusOptions}
                totalRecords={totalRecords}
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                onRowClick={handleRowClick}
                onContactSelect={handleContactSelect}
                onSelectAll={handleSelectAll}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onStatusFilterChange={handleStatusFilterChange}
                onDeleteSelectedContacts={handleBulkDelete}
            />

            {/* Upload Modal for CSV Upload */}
            <UploadContactsModal
                isOpen={isUploadModalOpen}
                onClose={handleUploadModalClose}
                workflows={workflows}
                onUploadSuccess={handleUploadSuccess}
                onUploadError={handleUploadError}
            />

            <NotificationModal
                isOpen={notificationModal.isOpen}
                onClose={closeNotificationModal}
                title={notificationModal.title}
                message={notificationModal.message}
                type={notificationModal.type}
                body={notificationModal.body}
                buttons={
                    notificationModal.buttons?.length
                        ? notificationModal.buttons
                        : [{ label: 'OK', onClick: closeNotificationModal }]
                }
            />

            {/* Contact Details Drawer */}
            <ContactDrawer
                isOpen={isDrawerOpen}
                contact={selectedContact}
                activeTab={activeTab}
                rawStatusOptions={rawStatusOptions}
                onClose={closeDrawer}
                onTabChange={handleTabChange}
            />
        </div>
    );
};

export default ContactManager;
